import { Component, ElementRef, Input, Renderer2 } from '@angular/core';
import { System, isNotNullOrUndefined, productProperty } from 'ngx-core-services';
import { map, shareReplay } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subject, combineLatest, of } from 'rxjs';
import { Store } from '@ngrx/store';

import { CreateCustomBlockSizeType, ICustomBlock } from '../decorators';
import { CreateBlockReduxSupport, IReduxBlock } from '../../store/helpers/decorator';
import { SystemsState, selectedSystem } from '../../store';
import { getLabels, buildHeatPumpProperties, getStatusDetails } from './heatpump-dashboard.utils';
@CreateBlockReduxSupport()
@CreateCustomBlockSizeType({
  'breakpoint-550': 550,
})
@Component({
  selector: 'lib-heatpump-dashboard',
  templateUrl: './heatpump-dashboard.component.html',
  styleUrls: ['./heatpump-dashboard.component.scss'],
})
export class HeatpumpDashboardComponent implements ICustomBlock, IReduxBlock {

  readonly _externalProperties$ = new BehaviorSubject<productProperty[] | undefined>(undefined);
  @Input() set externalDevices(properties: productProperty[] | undefined) {
    this._externalProperties$.next(properties);
  }

  @Input() uiFramework: 'material' | 'ionic' = 'material';

  @Input() productImageSrc?: string;

  readonly _instanceId$: Observable<string> = new Observable<string>();

  readonly _deviceId$ = new BehaviorSubject<string | undefined>(undefined);
  @Input() set deviceUid(value: string | undefined) {
    this._deviceId$.next(value);
  }

  @Input() refresh$ = new Subject<void>();
  _refresh$ = new Observable<boolean>();

  readonly system$ = new BehaviorSubject<System|undefined>(undefined);
  @Input() system?: System;

  _labels = getLabels();
  readonly labels$ = new BehaviorSubject<typeof this._labels>(this._labels);
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
    this.labels$.next(this._labels);
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  readonly selectedHeatPump$ = combineLatest([
    this._deviceId$.pipe(isNotNullOrUndefined()),
    this.store.select(selectedSystem).pipe(
      map(system => system?.monitoringType === 'AzzurroHub' ? system.data?.heatPump : undefined),
      isNotNullOrUndefined(),
    ),
    this._refresh$,
  ]).pipe(
    map(([deviceId, heatPumps, _]) => {
      const device = (heatPumps ?? []).find((hp: any) => hp.sn === deviceId);
      return device;
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly statusHeatPump$: Observable<productProperty | undefined> = combineLatest([
    this.selectedHeatPump$,
    this.labels$,
  ]).pipe(
    map(([device, labels]) => {
      const { label, cssClasses } = getStatusDetails(device?.globStatus ?? device?.globStatus, labels);
      return {
        name: labels.status,
        value: label,
        icon: 'info',
        iconCssClasses: 'text-grey-400',
        cssClasses,
      } as productProperty;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly selectedHeatPumpProperties$: Observable<productProperty[]> = combineLatest([
    this.selectedHeatPump$,
    this._externalProperties$.asObservable(),
    this.labels$,
  ]).pipe(
    map(([device, externalProps, labels]) => {
      const base = device ? buildHeatPumpProperties(device, labels) : [];
      return externalProps ? [...base, ...externalProps] : base;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  constructor(
    readonly store: Store<SystemsState>,
    readonly el: ElementRef,
    readonly renderer: Renderer2,
  ) { }
}
